import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const CODIGOS_TM = {
  '1': 'Stand By cliente',
  '2': 'Stand By falla',
  '3': 'Falta recurso SGA',
  '4': 'Factor climático',
  '5': 'DTM',
  '6': 'Espera ROC',
};

// Helpers
const hoy = () => new Date().toISOString().slice(0, 10);
const hace30 = () => {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};
const esTM = (pozo) => pozo && CODIGOS_TM[String(pozo).trim()];
const durFmt = (h) => h >= 1 ? `${Math.floor(h)}h ${String(Math.round((h % 1) * 60)).padStart(2, '0')}m` : `${Math.round(h * 60)}m`;

export default function HistorialEstados() {
  const { usuario } = useAuth();
  const [desde, setDesde] = useState(hace30());
  const [hasta, setHasta] = useState(hoy());
  const [sepFilter, setSepFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [codigoFilter, setCodigoFilter] = useState('');

  const [seps, setSeps] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ ensayos: 0, tm: 0, hEnsayo: 0, hTM: 0 });

  // Cargar lista de separadores al montar
  useEffect(() => {
    api.get('/separadores/historial-completo').then(r => {
      const defns = r.data.definiciones || {};
      setSeps(Object.entries(defns).map(([id, d]) => ({ id, nombre: d.nombre || id })));
    }).catch(() => {});
  }, []);

  // Cargar historial
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/separadores/historial-completo?desde=${desde}`);
      const historial = r.data.historial || {};
      const defns = r.data.definiciones || {};

      // Actualizar lista de seps por si no cargó
      if (seps.length === 0) {
        setSeps(Object.entries(defns).map(([id, d]) => ({ id, nombre: d.nombre || id })));
      }

      let todos = [];
      for (const [sepId, entries] of Object.entries(historial)) {
        if (sepFilter && sepId !== sepFilter) continue;
        const nombre = defns[sepId]?.nombre || sepId;

        for (const h of entries) {
          const inicio = h.inicio || '';
          const fin = h.fin || '';
          const pozo = h.pozo || '';

          // Filtro de fechas
          if (inicio && inicio.slice(0, 10) > hasta) continue;
          if (fin && fin.slice(0, 10) < desde) continue;
          if (!fin && inicio && inicio.slice(0, 10) < desde) continue;

          const tm = esTM(pozo);
          const codigo = tm ? pozo.trim() : null;

          // Filtro tipo
          if (tipoFilter === 'ensayo' && tm) continue;
          if (tipoFilter === 'tm' && !tm) continue;

          // Filtro código
          if (codigoFilter && codigo !== codigoFilter) continue;

          // Duración
          let horas = 0;
          if (inicio && fin) {
            horas = Math.max((new Date(fin) - new Date(inicio)) / 3600000, 0);
          } else if (h.duracion_seg) {
            horas = h.duracion_seg / 3600;
          }

          todos.push({
            sep_id: sepId,
            nombre,
            pozo,
            tipo: tm ? 'tm' : 'ensayo',
            codigo,
            desc: tm ? CODIGOS_TM[pozo.trim()] : pozo,
            inicio,
            fin: fin || null,
            horas: Math.round(horas * 100) / 100,
          });
        }
      }

      todos.sort((a, b) => (b.inicio || '').localeCompare(a.inicio || ''));
      setEventos(todos);

      // Stats
      const ensayos = todos.filter(e => e.tipo === 'ensayo');
      const tms = todos.filter(e => e.tipo === 'tm');
      setStats({
        ensayos: ensayos.length,
        tm: tms.length,
        hEnsayo: Math.round(ensayos.reduce((s, e) => s + e.horas, 0) * 10) / 10,
        hTM: Math.round(tms.reduce((s, e) => s + e.horas, 0) * 10) / 10,
      });
    } catch (err) {
      console.error('Error cargando historial:', err);
    }
    setLoading(false);
  }, [desde, hasta, sepFilter, tipoFilter, codigoFilter, seps.length]);

  useEffect(() => { cargar(); }, [cargar]);

  // Exportar XLS
  const exportar = async () => {
    try {
      const params = new URLSearchParams({ desde, hasta });
      if (sepFilter) params.set('sep', sepFilter);
      if (tipoFilter) params.set('tipo', tipoFilter);
      if (codigoFilter) params.set('codigo', codigoFilter);

      const r = await api.get(`/separadores/reporte-estados?${params}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      let fname = `estados_${desde}_${hasta}`;
      if (sepFilter) fname += `_${sepFilter}`;
      if (codigoFilter) fname += `_cod${codigoFilter}`;
      fname += '.xlsx';
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error exportando: ' + (err.message || err));
    }
  };

  // Volver al dashboard
  const volver = () => { window.location.href = '/'; };

  // Estilos inline — tema claro (consistente con Dashboard)
  const S = {
    page: { minHeight: '100vh', background: '#f8f9fa', color: '#1a1a1a', fontFamily: 'Inter,system-ui,sans-serif', padding: '20px 24px' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
    title: { fontSize: 22, fontWeight: 700, color: '#1a1a1a' },
    subtitle: { fontSize: 13, color: '#6b6b6b', marginTop: 2 },
    backBtn: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, color: '#6b6b6b', padding: '6px 14px', cursor: 'pointer', fontSize: 13 },
    filters: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20, padding: '16px', background: '#fff', borderRadius: 12, border: '1px solid #e0e0e0' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    label: { fontSize: 11, color: '#6b6b6b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { background: '#f8f9fa', border: '1px solid #d0d0d0', borderRadius: 6, color: '#1a1a1a', padding: '6px 10px', fontSize: 13, outline: 'none' },
    select: { background: '#f8f9fa', border: '1px solid #d0d0d0', borderRadius: 6, color: '#1a1a1a', padding: '6px 10px', fontSize: 13, outline: 'none', cursor: 'pointer' },
    btn: { padding: '7px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    btnPrimary: { background: '#2563eb', color: '#fff' },
    btnExport: { background: '#16a34a', color: '#fff' },
    stats: { display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' },
    statCard: (color) => ({ background: '#fff', border: `1px solid ${color}33`, borderRadius: 10, padding: '12px 20px', minWidth: 140 }),
    statVal: (color) => ({ fontSize: 22, fontWeight: 700, color }),
    statLabel: { fontSize: 11, color: '#6b6b6b', marginTop: 2 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', color: '#6b6b6b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 },
    td: (alt) => ({ padding: '7px 10px', borderBottom: '1px solid #e8e8e8', background: alt ? '#f3f4f6' : '#fff' }),
    badgeEnsayo: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#dcfce7', color: '#166534' },
    badgeTM: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#f3e8ff', color: '#7c3aed' },
    empty: { textAlign: 'center', padding: 40, color: '#999' },
    tableWrap: { maxHeight: 'calc(100vh - 340px)', overflowY: 'auto', borderRadius: 10, border: '1px solid #e0e0e0', background: '#fff' },
  };

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>Estados de Separadores</div>
          <div style={S.subtitle}>Historial de ensayos y tiempos muertos · {eventos.length} registros</div>
        </div>
        <button style={S.backBtn} onClick={volver}>← Dashboard</button>
      </div>

      {/* Filtros */}
      <div style={S.filters}>
        <div style={S.filterGroup}>
          <span style={S.label}>Desde</span>
          <input type="date" style={S.input} value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div style={S.filterGroup}>
          <span style={S.label}>Hasta</span>
          <input type="date" style={S.input} value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        <div style={S.filterGroup}>
          <span style={S.label}>Separador</span>
          <select style={S.select} value={sepFilter} onChange={e => setSepFilter(e.target.value)}>
            <option value="">Todos</option>
            {seps.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </div>
        <div style={S.filterGroup}>
          <span style={S.label}>Tipo</span>
          <select style={S.select} value={tipoFilter} onChange={e => setTipoFilter(e.target.value)}>
            <option value="">Todos</option>
            <option value="ensayo">Ensayo</option>
            <option value="tm">Tiempo muerto</option>
          </select>
        </div>
        <div style={S.filterGroup}>
          <span style={S.label}>Código TM</span>
          <select style={S.select} value={codigoFilter} onChange={e => setCodigoFilter(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(CODIGOS_TM).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={cargar} disabled={loading}>
            {loading ? 'Cargando...' : 'Buscar'}
          </button>
          <button style={{ ...S.btn, ...S.btnExport }} onClick={exportar}>
            Exportar XLS
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={S.stats}>
        <div style={S.statCard('#2563eb')}>
          <div style={S.statVal('#2563eb')}>{stats.ensayos}</div>
          <div style={S.statLabel}>Ensayos</div>
        </div>
        <div style={S.statCard('#2563eb')}>
          <div style={S.statVal('#60a5fa')}>{stats.hEnsayo}h</div>
          <div style={S.statLabel}>Horas ensayo</div>
        </div>
        <div style={S.statCard('#9333ea')}>
          <div style={S.statVal('#9333ea')}>{stats.tm}</div>
          <div style={S.statLabel}>Tiempos muertos</div>
        </div>
        <div style={S.statCard('#9333ea')}>
          <div style={S.statVal('#c084fc')}>{stats.hTM}h</div>
          <div style={S.statLabel}>Horas TM</div>
        </div>
      </div>

      {/* Tabla */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Separador</th>
              <th style={S.th}>Tipo</th>
              <th style={S.th}>Código</th>
              <th style={S.th}>Pozo / Motivo</th>
              <th style={S.th}>Inicio</th>
              <th style={S.th}>Fin</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Duración</th>
            </tr>
          </thead>
          <tbody>
            {eventos.length === 0 && !loading && (
              <tr><td colSpan={7} style={S.empty}>Sin registros en el período seleccionado</td></tr>
            )}
            {eventos.map((ev, i) => (
              <tr key={i}>
                <td style={S.td(i % 2)}>{ev.nombre}</td>
                <td style={S.td(i % 2)}>
                  <span style={ev.tipo === 'tm' ? S.badgeTM : S.badgeEnsayo}>
                    {ev.tipo === 'tm' ? 'Tiempo muerto' : 'Ensayo'}
                  </span>
                </td>
                <td style={{ ...S.td(i % 2), color: ev.codigo ? '#7c3aed' : '#6b6b6b', fontFamily: 'monospace' }}>
                  {ev.codigo || '—'}
                </td>
                <td style={{ ...S.td(i % 2), color: ev.tipo === 'tm' ? '#7c3aed' : '#1a1a1a' }}>
                  {ev.desc}
                </td>
                <td style={{ ...S.td(i % 2), fontFamily: 'monospace', fontSize: 12, color: '#6b6b6b' }}>
                  {ev.inicio ? ev.inicio.slice(0, 16).replace('T', ' ') : '—'}
                </td>
                <td style={{ ...S.td(i % 2), fontFamily: 'monospace', fontSize: 12, color: '#6b6b6b' }}>
                  {ev.fin ? ev.fin.slice(0, 16).replace('T', ' ') : '(en curso)'}
                </td>
                <td style={{ ...S.td(i % 2), textAlign: 'right', fontWeight: 600, color: ev.tipo === 'tm' ? '#7c3aed' : '#16a34a' }}>
                  {durFmt(ev.horas)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', color: '#999', fontSize: 11, marginTop: 16 }}>
        SepManager — Rocket Tech S.A.S · Performance SRL
      </div>
    </div>
  );
}
